import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

const MAX_SCALE = 5;
const DOUBLE_TAP_SCALE = 2.5;
const TAP_MS = 280;
const PAGE_MS = 280;
const EASE = "cubic-bezier(.2,.8,.2,1)";
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const rubber = (v, lim) => (v > lim ? lim + (v - lim) * 0.35 : v < -lim ? -lim + (v + lim) * 0.35 : v);

// iPhone-Photos-style gestures for the fullscreen viewer, written straight to DOM styles so
// they track the finger at 60fps: pinch / double-tap zoom, pan while zoomed, swipe left/right
// to page (rubber-band at the ends), swipe down to close, single tap toggles the chrome.
export function useViewerGestures({ index, count, zoomable, onNav, onClose, onToggleChrome }) {
  const rootRef = useRef(null);
  const trackRef = useRef(null);
  const mediaRef = useRef(null);
  const pts = useRef(new Map());
  const g = useRef({ mode: null });
  const z = useRef({ s: 1, x: 0, y: 0 });
  const off = useRef({ x: 0, y: 0 });
  const tap = useRef({ last: null, timer: null });
  const busy = useRef(false);
  const wheelX = useRef({ sum: 0, t: 0 });
  const latest = useRef({});
  latest.current = { index, count, zoomable, onNav, onClose, onToggleChrome };

  const size = () => {
    const r = rootRef.current;
    return { w: r?.clientWidth || window.innerWidth, h: r?.clientHeight || window.innerHeight };
  };
  const applyZoom = (animate) => {
    const el = mediaRef.current;
    if (!el) return;
    el.style.transition = animate ? `transform 260ms ${EASE}` : "none";
    el.style.transform = `translate3d(${z.current.x}px, ${z.current.y}px, 0) scale(${z.current.s})`;
  };
  const applyTrack = (animate) => {
    const t = trackRef.current;
    const r = rootRef.current;
    if (!t) return;
    const fade = clamp(off.current.y / 500, 0, 0.75);
    t.style.transition = animate ? `transform ${PAGE_MS}ms ${EASE}` : "none";
    t.style.transform = `translate3d(${off.current.x}px, ${off.current.y}px, 0) scale(${1 - fade * 0.3})`;
    if (r) {
      r.style.transition = animate ? `background-color ${PAGE_MS}ms` : "none";
      r.style.backgroundColor = `rgba(0,0,0,${1 - fade})`;
    }
  };
  // how far a zoomed photo may pan — the picture's displayed size, not its letterbox
  const bounds = (s) => {
    const { w, h } = size();
    let cw = w;
    let ch = h;
    const img = mediaRef.current?.querySelector("img");
    if (img?.naturalWidth) {
      const k = Math.min(w / img.naturalWidth, h / img.naturalHeight);
      cw = img.naturalWidth * k;
      ch = img.naturalHeight * k;
    }
    return { x: Math.max(0, (cw * s - w) / 2), y: Math.max(0, (ch * s - h) / 2) };
  };
  const clampPan = () => {
    const b = bounds(z.current.s);
    z.current.x = clamp(z.current.x, -b.x, b.x);
    z.current.y = clamp(z.current.y, -b.y, b.y);
  };
  const settleZoom = () => {
    if (z.current.s <= 1.01) z.current = { s: 1, x: 0, y: 0 };
    else {
      z.current.s = Math.min(z.current.s, MAX_SCALE);
      clampPan();
    }
    applyZoom(true);
  };
  // zoom to s keeping the point (px, py) — relative to the screen centre — under the finger
  const zoomAt = (s, px, py) => {
    const k = s / z.current.s;
    z.current = { s, x: px - (px - z.current.x) * k, y: py - (py - z.current.y) * k };
  };

  const page = useCallback((dir) => {
    const { index: i, count: n, onNav: nav } = latest.current;
    if (busy.current) return;
    if (i + dir < 0 || i + dir >= n) {
      off.current = { x: 0, y: 0 };
      applyTrack(true);
      return;
    }
    busy.current = true;
    off.current = { x: -dir * size().w, y: 0 };
    applyTrack(true);
    setTimeout(() => nav(dir), PAGE_MS);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTap = (x, y) => {
    const t = tap.current;
    const now = Date.now();
    if (t.last && now - t.last.t < TAP_MS && Math.hypot(x - t.last.x, y - t.last.y) < 30) {
      clearTimeout(t.timer);
      t.last = null;
      if (!latest.current.zoomable) return;
      const { w, h } = size();
      if (z.current.s > 1.01) z.current = { s: 1, x: 0, y: 0 };
      else {
        zoomAt(DOUBLE_TAP_SCALE, x - w / 2, y - h / 2);
        clampPan();
      }
      applyZoom(true);
      return;
    }
    t.last = { t: now, x, y };
    clearTimeout(t.timer);
    t.timer = setTimeout(() => {
      t.last = null;
      latest.current.onToggleChrome?.();
    }, TAP_MS);
  };

  const onPointerDown = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    if (e.target.closest?.("button, a, input, [data-no-gesture]")) return;
    pts.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (e.target.tagName !== "VIDEO") {
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* pointer already gone */ }
    }
    const { w, h } = size();
    const now = Date.now();
    if (pts.current.size === 1) {
      g.current = { mode: "pending", sx: e.clientX, sy: e.clientY, x0: z.current.x, y0: z.current.y,
        lx: e.clientX, ly: e.clientY, lt: now, vx: 0, vy: 0 };
    } else if (pts.current.size === 2 && latest.current.zoomable) {
      const [a, b] = [...pts.current.values()];
      if (g.current.mode === "swipe" || g.current.mode === "dismiss") {
        off.current = { x: 0, y: 0 };
        applyTrack(true);
      }
      g.current = { mode: "pinch", d0: Math.hypot(a.x - b.x, a.y - b.y) || 1, s0: z.current.s, x0: z.current.x, y0: z.current.y,
        mx: (a.x + b.x) / 2 - w / 2, my: (a.y + b.y) / 2 - h / 2 };
    }
  };

  const onPointerMove = (e) => {
    const p = pts.current.get(e.pointerId);
    if (!p) return;
    p.x = e.clientX;
    p.y = e.clientY;
    const st = g.current;
    const { w, h } = size();
    if (st.mode === "pinch") {
      if (pts.current.size < 2) return;
      const [a, b] = [...pts.current.values()];
      let s = (st.s0 * Math.hypot(a.x - b.x, a.y - b.y)) / st.d0;
      s = s > MAX_SCALE ? MAX_SCALE + (s - MAX_SCALE) * 0.25 : s < 1 ? 1 - (1 - s) * 0.5 : s;
      const k = s / st.s0;
      const mx = (a.x + b.x) / 2 - w / 2;
      const my = (a.y + b.y) / 2 - h / 2;
      z.current = { s, x: mx - (st.mx - st.x0) * k, y: my - (st.my - st.y0) * k };
      applyZoom(false);
      return;
    }
    const dx = e.clientX - st.sx;
    const dy = e.clientY - st.sy;
    const now = Date.now();
    const dt = Math.max(1, now - st.lt);
    st.vx = (e.clientX - st.lx) / dt;
    st.vy = (e.clientY - st.ly) / dt;
    st.lx = e.clientX;
    st.ly = e.clientY;
    st.lt = now;
    if (st.mode === "pending") {
      if (Math.hypot(dx, dy) < 8) return;
      st.mode = z.current.s > 1.01 ? "pan" : Math.abs(dx) >= Math.abs(dy) ? "swipe" : dy > 0 ? "dismiss" : "none";
    }
    if (st.mode === "pan") {
      const b = bounds(z.current.s);
      z.current.x = rubber(st.x0 + dx, b.x);
      z.current.y = rubber(st.y0 + dy, b.y);
      applyZoom(false);
    } else if (st.mode === "swipe") {
      const { index: i, count: n } = latest.current;
      const edge = (dx > 0 && i === 0) || (dx < 0 && i === n - 1);
      off.current = { x: edge ? dx * 0.3 : dx, y: 0 };
      applyTrack(false);
    } else if (st.mode === "dismiss") {
      off.current = { x: dx * 0.4, y: Math.max(0, dy) };
      applyTrack(false);
    }
  };

  const onPointerEnd = (e) => {
    if (!pts.current.has(e.pointerId)) return;
    pts.current.delete(e.pointerId);
    const st = g.current;
    if (st.mode === "pinch") {
      if (pts.current.size === 1) {
        const [p] = [...pts.current.values()];
        const zoomed = z.current.s > 1.01;
        g.current = { mode: zoomed ? "pan" : "none", sx: p.x, sy: p.y, x0: z.current.x, y0: z.current.y,
          lx: p.x, ly: p.y, lt: Date.now(), vx: 0, vy: 0 };
        if (!zoomed) settleZoom(); // pinched below fit — spring back now, not after the last finger
      } else if (!pts.current.size) {
        g.current = { mode: null };
        settleZoom();
      }
      return;
    }
    if (pts.current.size) return;
    g.current = { mode: null };
    if (st.mode === "pending" && e.type === "pointerup") handleTap(e.clientX, e.clientY);
    else if (st.mode === "pan" || st.mode === "none") settleZoom();
    else if (st.mode === "swipe") {
      const w = size().w;
      if (off.current.x < -w * 0.18 || st.vx < -0.45) page(1);
      else if (off.current.x > w * 0.18 || st.vx > 0.45) page(-1);
      else {
        off.current = { x: 0, y: 0 };
        applyTrack(true);
      }
    } else if (st.mode === "dismiss") {
      if (off.current.y > 110 || st.vy > 0.5) latest.current.onClose();
      else {
        off.current = { x: 0, y: 0 };
        applyTrack(true);
      }
    }
  };

  // a new photo always opens at fit-to-screen, centred
  useLayoutEffect(() => {
    off.current = { x: 0, y: 0 };
    applyTrack(false);
    z.current = { s: 1, x: 0, y: 0 };
    busy.current = false;
  }, [index]); // eslint-disable-line react-hooks/exhaustive-deps

  // native listeners: trackpad pinch / wheel, and no page zoom or scroll behind the viewer (iOS)
  useEffect(() => {
    const r = rootRef.current;
    if (!r) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const { w, h } = size();
      if ((e.ctrlKey || e.metaKey) && latest.current.zoomable) {
        const s = clamp(z.current.s * Math.exp(-e.deltaY * 0.01), 1, MAX_SCALE);
        if (s <= 1) z.current = { s: 1, x: 0, y: 0 };
        else {
          zoomAt(s, e.clientX - w / 2, e.clientY - h / 2);
          clampPan();
        }
        applyZoom(false);
      } else if (z.current.s > 1.01) {
        z.current.x -= e.deltaX;
        z.current.y -= e.deltaY;
        clampPan();
        applyZoom(false);
      } else if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        const now = Date.now();
        const acc = wheelX.current;
        acc.sum = now - acc.t > 400 ? e.deltaX : acc.sum + e.deltaX;
        acc.t = now;
        if (Math.abs(acc.sum) > 80) {
          page(acc.sum > 0 ? 1 : -1);
          acc.sum = 0;
          acc.t = now + 400;
        }
      }
    };
    const block = (e) => { if (e.target?.tagName !== "VIDEO") e.preventDefault(); };
    const noGesture = (e) => e.preventDefault();
    r.addEventListener("wheel", onWheel, { passive: false });
    r.addEventListener("touchmove", block, { passive: false });
    document.addEventListener("gesturestart", noGesture);
    document.addEventListener("gesturechange", noGesture);
    const t = tap.current;
    return () => {
      r.removeEventListener("wheel", onWheel);
      r.removeEventListener("touchmove", block);
      document.removeEventListener("gesturestart", noGesture);
      document.removeEventListener("gesturechange", noGesture);
      clearTimeout(t.timer);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    rootRef, trackRef, mediaRef, page,
    handlers: { onPointerDown, onPointerMove, onPointerUp: onPointerEnd, onPointerCancel: onPointerEnd },
  };
}
